export type TransactionType = {
  id: string;
  date: string;
  amount: string;
  description: string;
};

export type CurrencyValueType = {
  value: string;
  currency: string;
};

export type DigitalCardSummaryType = {
  id: number;
  userName: string;
  productName: string;
  productExternalCode: string;
  cardLast4: string;
  creditLimit: CurrencyValueType;
};

export type UserDataType = {
  isFirstTime: boolean;
  rewardBalance: string;
  rewardRedumptionBonus: string;
  currentBalance: CurrencyValueType;
  availableCredit: string;
  paymentDue?: string;
  statementBalance: CurrencyValueType;
  minimumPayment?: string;
  previousStatementDate?: string;
  lastPaymentDate?: string;
  paymentDueDate?: string;
  lastPayment?: CurrencyValueType;
  rewardsBalance?: CurrencyValueType;
  redemptionBonus?: CurrencyValueType;
  digitalCardSummary: DigitalCardSummaryType;
  transactionList: TransactionType[];
};

export interface AsyncStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  clear(): Promise<void>;
}

export enum CommonCardCategory {
  ACCOUNT = 'account',
  STATEMENT = 'statement',
  TRANSACTION = 'transaction',
}

export interface CommonDataTileType {
  type: CommonCardCategory;
  title: string;
  chipTitleOne?: string;
  chipValueOne?: string;
  chipTitleTwo?: string;
  chipValueTwo?: string;
  infoText?: string;
  onInfoClick?: () => void;
}

export enum TransactionStatus {
  PENDING = 'PENDING',
  DECLINED = 'DECLINED',
  APPROVED = 'APPROVED',
}

export type SingleTransaction = {
  vendor: string;
  status: TransactionStatus;
  amount: string;
};

export type FUserDataType = {
  isFirstTime: boolean;
  rewardBalance: string;
  rewardRedumptionBonus: string;
  currentBalance: string;
  availableCredit: string;
  paymentDue?: string;
  statementBalance?: string;
  minimumPayment?: string;
  transactionList: Transactions[];
  digitalCardSummary: DigitalCardSummaryType;
};

export interface Transactions {
  postedDate: string;
  data: SingleTransaction[];
}

export interface DashboardTitleWrapperType {
  type: CommonCardCategory;
  transactions?: Activities[];
  onPress: () => void;
  children: React.ReactNode;
}

export type CardContentType = {
  ACD_TITLE: (val: string) => string;
  ACD_CHIP_TITLE_ONE: string;
  ACD_CHIP_TITLE_TWO: string;

  STMT_TITLE: (val: string) => string;
  STMT_CHIP_TITLE_ONE: string;
  STMT_CHIP_TITLE_TWO: string;

  RECENT_TRANSACTIONS: string;

  CTA_ACC_DETAILS: string;
  CTA_STATEMENTS: string;
  CTA_ALL_TRANSACTIONS: string;
};

export type UserTransactionType = {
  statusCode: number;
  activitySummary: ActivitySummaryType;
};

export type UserRedeemTransactionType = {
  statusCode: number;
  transactions: RedeemTransaction[];
};

export type RewardsPromotion = {
  id: string;
  name: string;
  pointType: string;
  pointsEarned: number;
  rewardsEarned: number;
  promoEligible?: boolean;
};

export type RewardsTransaction = {
  sequenceNumber: number;
  date: string;
  description: string;
  merchantCategoryCode: string;
  type: string;
  productCode: string;
  basePointsEarned: number;
  transactionAmount: number;
  transactionCurrencyCode: string;
  settlementAmount: number;
  settlementCurrencyCode: string;
  billingCurrencyCode: string;
  billingAmount: number;
  postingDate: string;
  processedDate: string;
  redemptionId: string;
  promotions: RewardsPromotion[];
};

export type UserRewardsHistoryType = {
  statusCode: number;
  rewardsHistorySummary: {
    offset: number;
    limit: number;
    total: number;
    count: number;
    accountId: string;
    customerId: string;
    last4BankAccountNumber: number;
    transactions: RewardsTransaction[];
  };
};

export type UserRewardPointsDetailsType = {
  statusCode: number;
  pointsDetailsResponse: {
    pointsDetails: {
      availablePoints: number;
      onHoldPoints: number;
    };
    rewardBalance: number;
    redemptionBonusAmount: number;
  };
};

export type AdjustmentDetails = {
  householdId: string;
  accountId: string;
  customerId: string;
};

export type UserRewardPointsAdjustmentsDetailsType = {
  statusCode: number;
  pointsAdjustmentsDetails: {
    adjustmentDetails: AdjustmentDetails[];
  };
};

export type RedeemTransaction = {
  de004TransactionAmount: string;
  de005IssuerCurrencySettlementAmount: string;
  de006CardholderBillingAmount: string;
  de012TransactionTime: string;
  de013TransactionDate: string;
  de018MerchantCode: string;
  de032AcquiringInstitutionIdCode: string;
  de038AuthIdResponse: string | null;
  de039ResponseCode: string;
  de042CardAcceptorIdCode: string;
  de043CardAcceptorNameLocation: string;
  de048TCC: string;
  de049TransactionCurrencyCode: string;
  de050SettlementCurrencyCode: string;
  de051CardholderBillingCurrencyCode: string;
  id: string;
  merchantCategoryName: string;
  reprocessId: string | null;
  reprocessedBy: string | null;
  reprocessedDateTime: string | null;
  responseReasonDesc: string;
  responseReasonId: string;
  externalId: string | null;
  transactionDateTime: string;
};
export type ActivitySummaryType = {
  id: number;
  activities: Activities[];
};

export type Activities = {
  date: string;
  amount: AmountType;
  activityCategory: string;
  activityClassification: string;
  merchant: MerchantType;
  activityCategoryCode?: string;
  postedDate?: string;
  installmentTransactionStatus?: string;
  referenceNumber?: string;
  activityId?: string;
  activityStatus: string;
  time?: string;
  activityType: string;
  cardNumber: string;
};

export type AmountType = {
  value: string;
  currency: string;
};

export type MerchantType = {
  name?: string;
  address?: AddressType;
  categoryCode?: string;
  category?: string;
  categoryDescription?: string;
};

export type AddressType = {
  stateProvince?: string;
  city?: string;
  countryCode?: string;
  postalCode?: string;
};

export type RecentTransactionType = {
  statusCode: number;
  activitySummary: ActivitySummary;
};

export type ActivitySummary = {
  id: number;
  activities: Activity[];
};

export type Activity = {
  date: string;
  amount: Amount;
  activityCategory: string;
  activityClassification: string;
  merchant: Merchant;
  activityId?: string;
  activityStatus: string;
  time?: string;
  activityType: string;
  cardNumber: string;
  activityCategoryCode?: string;
  postedDate?: string;
  installmentTransactionStatus?: string;
  referenceNumber?: string;
  foreign?: ForeignExchange; // Assuming there's a ForeignExchange type defined elsewhere
  ancillaryServiceCharges?: AncillaryServiceCharge[]; // Assuming there's an AncillaryServiceCharge type defined elsewhere
};

export type ForeignExchange = {
  conversionMarkupRate: string; // e.g., "0.990099009"
  markupRate: string; // e.g., "1"
  originalAmount: Amount; // Assuming Amount is already defined as { currency: string; value: string }
  exchangeFee: Amount; // Assuming Amount is already defined as { currency: string; value: string }
  markupAmount: Amount; // Assuming Amount is already defined as { currency: string; value: string }
  conversionRate: string; // e.g., "1"
};

export type AncillaryServiceCharge = {
  feeAmount: Amount; // Assuming Amount is already defined as { currency: string; value: string }
  feeType: string; // e.g., "CARGO", "OTHER", "GIFT_CARD", "CHANGE_FEE"
};

export type Amount = {
  value: string;
  currency: string;
};

export type Merchant = {
  name: string;
  address?: Address;
  categoryCode?: string;
  category: string;
  categoryDescription?: string;
};

export type Address = {
  stateProvince?: string;
  city?: string;
  countryCode?: string;
  postalCode: string;
};
// Get card status model

export type GetCardStatus = {
  id: number;
  cardIsLocked: boolean;
};

// Update card status model

export type UpdateCardStatus = {
  statusCode: number;
  cardResponse: CardResponse;
};

export type CardResponse = {
  id: number;
  confirmationNumber: string;
};

// Card response body model

export type CardBodyModel = {
  accountId: number;
  customerId: number;
  cardIsLocked: boolean;
};

export interface MerchantAddress {
  city?: string;
  countryCode?: string;
  postalCode?: string;
  stateProvince?: string;
}

export interface TransactionHistoryType {
  statusCode: number;
  activities: Activities[];
}

// EPP types
export type installMentPlans = {
  planType: string;
  outstandingInstallmentAmount: {
    currency: string;
    value: string;
  };
  outstandingInstallments: number | string;
  planName: string;
  planStatus: string;
  remainingBalanceAfterExpiration: {
    currency: string;
    value: string;
  };
  totalNumberOfInstallments: number | string;
  currentDueInstallmentAmount: {
    currency: string;
    value: string;
  };
  outstandingFinancialAmount: {
    currency: string;
    value: string;
  };
  planExpiration: {
    expirationDate: string;
    expiredDate?: string;
  };
  originalTransactionAmount: {
    currency: string;
    value: string;
  };
  refNumber: string;
  optionCode: string;
  numberOfMonthsBetweenPayments: number | string;
  numberOfInstallmentsPaid: number | string;
  planId: string;
  optionId: number | string;
  activityType: string;
  outstandingTransactionAmount: {
    currency: string;
    value: string;
  };
  apr: string;
  principleCompleted: number;
  completedPercentage: number;
  noOfPaymentsRemain: number | string;
  nextPaymentDate: string;
  statusReason?: string;
};
export type InstallmentPlanType = {
  statusCode: number;
  installData: {
    id: number;
    installmentPlans: installMentPlans[];
  };
};

export type InstallmentPlanResponseType = InstallmentPlanType;

//Authentication
export type AuthContextType = {
  isAuthenticated: boolean;
  login: () => void;
  logout: () => void;
};

export type AuthLoginResponseType = {
  headers: any;
  statusCode: number;
  authData?: {
    userName: string;
    authenticated: boolean;
    accounts?: Array<AccountType>;
  };
  message?: string;
};

export type AccountType = {
  accountId: string;
  productName: string;
  creditLimit: {
    value: string;
    currency: string;
  };
};

export type LoginPayload = {
  username: string;
  password: string;
  deviceId: string;
  channel: string;
};

export interface SignInApiError {
  errCode: string;
  msg: string;
  statusCode?: string | number;
}
export type RewardsType = {
  message: string;
  statusCode: number;
  rewardsResponse: RewardsResponse;
};

export type RewardsResponse = {
  rewardsBalance: RewardsBalance;
  redemptionBonus: RedemptionBonus;
  id: number;
};

export type RewardsBalance = {
  value: string;
  currency: string;
};

export type RedemptionBonus = {
  value: string;
  currency: string;
};

//Add card to wallet popup

//Apple wallet response
export type AddCardToAppleWalletResponseType = {
  encryptedPayLoad: string;
  encryptedPassData: string;
  ephemeralPublicKey: string;
};
export type AddCardToGoogleWalletResponseType = {
  encryptedPayLoad: string;
  address: {
    address1: string;
    address2: string;
    countryCode: string;
    postalCode: string;
    name: string;
    locality: string;
    administrativeArea: string;
  };
};

export type AddCardToAppleWalletRequestBody = {
  isVirtualCard?: boolean;
  nonceSignature: string;
  certificates: string[];
  nonce: string;
  virtualCard?: boolean;
};

export type AddCardToGoogleWalletRequestBody = {
  isVirtualCard?: boolean;
  walletAccountId?: string;
  applicationId: string;
  deviceId: string;
  virtualCard?: boolean;
};

export type UserRedeemersPreferencesType = {
  statusCode: number;
  preferences: {
    emailAddress: string;
    fixedAccountRedemptionAmount: number;
    minAccountPurchaseThreshold: number;
    receiveEmailSw: boolean;
    receiveSMSSw: boolean;
    redemptionFrequency: string;
    smsNumber: string;
  };
};

export type RedeemRewardsAuthorization = {
  de004TransactionAmount: string;
  de005IssuerCurrencySettlementAmount: string;
  de006CardholderBillingAmount: string;
  de012TransactionTime: string;
  de013TransactionDate: string;
  de018MerchantCode: string;
  de032AcquiringInstitutionIdCode: string;
  de038AuthIdResponse: string;
  de022PoSEntryMode: string;
  de039ResponseCode: string;
  de042CardAcceptorIdCode: string;
  de043CardAcceptorNameLocation: string;
  de048TCC: string;
  de049TransactionCurrencyCode: string;
  de050SettlementCurrencyCode: string;
  de051CardholderBillingCurrencyCode: string;
  id: string;
  merchantCategoryName: string;
  responseReasonDesc: string;
  responseReasonId: string;
  externalId: string;
  transactionDateTime: string;
  cashBackAmount: string;
  pointsRedeemed: string;
  partiallyRedeemed?: boolean;
  merchantCountry?: string;
  totalPointsRequired?: number | string;
  eligibleRogersRedeemBonus: boolean;
  reprocessEligible: boolean;
};

export type AdjustmentsType = {
  adjustmentDate: string;
  adjustmentDescription: string;
  adjustmentReason: string;
  adjustmentValue: number;
};
